(() => {
  'use strict';

  const WIDGET_SELECTOR = 'purchase-options';
  const CART_FORM_SELECTOR = 'form[action*="/cart/add"]';
  const PRODUCT_ROOT_SELECTOR = '#main .shopify-section--main-product, quick-buy-modal';
  const BRIDGE_INPUT_ATTRIBUTE = 'data-pfm-stayai-selling-plan';
  const BRIDGE_OWNER_ATTRIBUTE = 'data-pfm-stayai-owner';
  let syncFrame = 0;
  const observedRoots = new WeakSet();

  function productRoots() {
    return document.querySelectorAll(PRODUCT_ROOT_SELECTOR);
  }

  function isProductForm(form) {
    return Boolean(
      form &&
        form.matches(CART_FORM_SELECTOR) &&
        form.querySelector('input[name="id"]') &&
        form.querySelector('button[type="submit"], input[type="submit"]'),
    );
  }

  function findProductForm(widget) {
    const requestedFormId = widget.getAttribute('productformid');
    const requestedForm = requestedFormId ? document.getElementById(requestedFormId) : null;

    if (isProductForm(requestedForm)) return requestedForm;

    const scopes = [widget.closest('.product-info'), widget.closest('.shopify-section')].filter(Boolean);

    for (const scope of scopes) {
      const form = Array.from(scope.querySelectorAll(CART_FORM_SELECTOR)).find(isProductForm);
      if (form) return form;
    }

    return null;
  }

  function selectedPurchase(widget) {
    return widget.querySelector('input[name="purchaseType"]:checked')?.value || '';
  }

  function selectedPlan(widget) {
    return widget.querySelector('select[name="stayai_selling_plan"]')?.value?.trim() || '';
  }

  function clearSellingPlan(form) {
    form.querySelectorAll(`input[name="selling_plan"]`).forEach((input) => {
      if (input.hasAttribute(BRIDGE_INPUT_ATTRIBUTE)) {
        input.remove();
        return;
      }

      input.value = '';
      input.disabled = true;
      input.dataset.pfmStayAiSuppressed = 'true';
    });

    form.dataset.pfmStayAiPurchase = 'one_time';
    delete form.dataset.pfmStayAiSellingPlan;
  }

  function setSellingPlan(form, widget, planId) {
    const inputs = Array.from(form.querySelectorAll('input[name="selling_plan"]'));
    let input =
      inputs.find((candidate) => !candidate.hasAttribute(BRIDGE_INPUT_ATTRIBUTE)) ||
      inputs.find((candidate) => candidate.hasAttribute(BRIDGE_INPUT_ATTRIBUTE));

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      input.setAttribute(BRIDGE_INPUT_ATTRIBUTE, '');
      input.setAttribute(BRIDGE_OWNER_ATTRIBUTE, widget.id || 'stayai');
      form.append(input);
    }

    input.value = planId;
    input.disabled = false;
    delete input.dataset.pfmStayAiSuppressed;

    inputs.forEach((candidate) => {
      if (candidate === input) return;
      candidate.disabled = true;
      candidate.dataset.pfmStayAiDuplicate = 'true';
    });

    form.dataset.pfmStayAiPurchase = 'subscription';
    form.dataset.pfmStayAiSellingPlan = planId;
  }

  function syncWidget(widget) {
    if (!(widget instanceof HTMLElement)) return;

    const form = findProductForm(widget);
    if (!form) return;

    const formId = form.getAttribute('id') || '';
    if (formId && widget.getAttribute('productformid') !== formId) {
      widget.setAttribute('productformid', formId);
    }

    const purchase = selectedPurchase(widget);
    const planId = selectedPlan(widget);

    if (purchase === 'purchaseTypeSubscription' && planId) {
      setSellingPlan(form, widget, planId);
    } else if (purchase === 'purchaseTypeOneTime') {
      clearSellingPlan(form);
    }

    widget.dataset.pfmStayAiBridge = 'ready';
  }

  function cleanOrphanedInputs(root) {
    root.querySelectorAll(`input[${BRIDGE_INPUT_ATTRIBUTE}]`).forEach((input) => {
      const ownerId = input.getAttribute(BRIDGE_OWNER_ATTRIBUTE);
      if (ownerId && ownerId !== 'stayai' && !document.getElementById(ownerId)) input.remove();
    });
  }

  function syncAll() {
    syncFrame = 0;
    productRoots().forEach((root) => {
      cleanOrphanedInputs(root);
      root.querySelectorAll(WIDGET_SELECTOR).forEach(syncWidget);
    });
  }

  function scheduleSync() {
    if (syncFrame) return;
    syncFrame = window.requestAnimationFrame(syncAll);
  }

  function widgetForForm(form) {
    const scope = form.closest('.product-info') || form.closest('.shopify-section');
    return scope?.querySelector(WIDGET_SELECTOR) || null;
  }

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.closest(PRODUCT_ROOT_SELECTOR) || !isProductForm(form)) return;

      const widget = widgetForForm(form);
      if (widget) syncWidget(widget);
    },
    true,
  );

  document.addEventListener('change', (event) => {
    if (
      event.target instanceof Element &&
      event.target.matches(
        `${WIDGET_SELECTOR} input[name="purchaseType"], ${WIDGET_SELECTOR} select[name="stayai_selling_plan"], variant-picker input`,
      )
    ) {
      scheduleSync();
    }
  });

  document.addEventListener('variant:change', scheduleSync);
  document.addEventListener('product:rerender', scheduleSync);
  document.addEventListener('shopify:section:load', () => {
    observeProductRoots();
    scheduleSync();
  });
  document.addEventListener(
    'dialog:before-show',
    (event) => {
      if (!(event.target instanceof Element) || !event.target.matches('quick-buy-modal')) return;
      observeProductRoot(event.target);
      scheduleSync();
    },
    true,
  );

  const observer = new MutationObserver((records) => {
    const touchesProductInfo = records.some((record) => {
      const target = record.target instanceof Element ? record.target : record.target.parentElement;
      if (target?.closest(WIDGET_SELECTOR)) return true;

      return [...record.addedNodes, ...record.removedNodes].some(
        (node) =>
          node instanceof Element &&
          (node.matches(`${WIDGET_SELECTOR}, .product-info, ${CART_FORM_SELECTOR}`) ||
            node.querySelector(`${WIDGET_SELECTOR}, .product-info, ${CART_FORM_SELECTOR}`)),
      );
    });

    if (touchesProductInfo) scheduleSync();
  });

  function observeProductRoot(root) {
    if (!root || observedRoots.has(root)) return;

    observedRoots.add(root);
    observer.observe(root, { childList: true, subtree: true });
  }

  function observeProductRoots() {
    productRoots().forEach(observeProductRoot);
  }

  function start() {
    syncAll();
    observeProductRoots();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
